import { useState, useEffect } from 'react';
import { Mail, ArrowRight, ExternalLink, Clock, CheckCircle } from 'lucide-react';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

export default function EmailVerificationModal({ isOpen, onClose, email }: EmailVerificationModalProps) {
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleResendEmail = async () => {
    setIsResending(true);
    setCountdown(60);
    
    try {
      // API Call: POST /api/auth/resend-verification/
      const payload = { email };
      console.log('Resend verification payload:', payload);
      // await api.auth.resendVerification(payload);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      console.error('Resend error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckSpam = () => {
    // Open email client or redirect to spam folder
    window.open(`mailto:${email}`, '_blank');
  };

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Verify Your Email</h2>
                <p className="text-sm text-gray-600">Check your inbox for the link</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6L12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Email Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Sent to:</p>
                  <p className="font-mono text-sm text-blue-700 break-all">{email}</p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Next steps:</p>
                  <ol className="text-sm text-amber-800 list-decimal list-inside space-y-2 mt-2">
                    <li>Check your <strong>inbox</strong> and spam folder</li>
                    <li>Click the verification link</li>
                    <li>Link expires in <strong>24 hours</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Resend Section */}
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">
                Haven't received it? 
                {countdown > 0 && (
                  <span className="text-blue-600"> You can resend in {countdown}s</span>
                )}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleResendEmail}
                  disabled={isResending || countdown > 0}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isResending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Resending...</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      <span>Resend email</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleCheckSpam}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors text-gray-700"
                >
                  <Mail className="w-4 h-4" />
                  <span>Check spam folder</span>
                </button>
              </div>
            </div>

            {/* Success Message */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">Email verified!</p>
                  <p className="text-sm text-green-700">You can now sign in.</p>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <button
                onClick={() => window.open('https://gmail.com', '_blank')}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>Open Gmail</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
